import useDateFormatters from "@hooks/useDateFormatters"

import {
    InvoicePaymentBadge,
    IssueDateBadge,
    LabelsBadges,
    LastModifiedBadge,
    TermsBadge,
    TotalBadge,
} from "@molecules/ObjectCard/Attributes/AttributeBadges"
import ObjectCard from "@molecules/ObjectCard/ObjectCard"
import { composeClientLocation, shouldShowAttribute } from "@molecules/ObjectCard/ObjectCards/ObjectCards.utils"
import ObjectStatusBadge from "@molecules/ObjectStatusBadge/ObjectStatusBadge"

import { InvoiceCardProps } from "./InvoiceCard.types"

type InvoiceTimestampKey =
    | "created_at"
    | "last_sent_at"
    | "last_paid_at"
    | "last_voided_at"
    | "last_forgiven_at"
    | "last_status_transition_time"

const STATUS_TRANSITION: Record<Invoice["status_label"], { verb: string; timestampKey: InvoiceTimestampKey }> = {
    Draft: { verb: "Created", timestampKey: "created_at" },
    Unsent: { verb: "Created", timestampKey: "created_at" },
    Outstanding: { verb: "Sent", timestampKey: "last_sent_at" },
    Overdue: { verb: "Sent", timestampKey: "last_sent_at" },
    Paid: { verb: "Paid", timestampKey: "last_paid_at" },
    Voided: { verb: "Voided", timestampKey: "last_voided_at" },
    Forgiven: { verb: "Forgiven", timestampKey: "last_forgiven_at" },
}

// The paid status badge only applies once an invoice is in a billable lifecycle.
function isPayable(status: Invoice["status_label"]): boolean {
    return status === "Paid" || status === "Forgiven" || status === "Outstanding" || status === "Overdue"
}

// Terms only run overdue while the invoice is open; `is_overdue` is date-based alone, so a closed invoice past its
// due date would otherwise show the overdue look.
const OPEN_STATUS_LABELS: Invoice["status_label"][] = ["Draft", "Unsent", "Outstanding", "Overdue"]

export default function InvoiceCard(props: InvoiceCardProps) {
    const { invoice, displayedAttributes, onClick, isLoading = false, isTabbable, className, ...errorProps } = props

    const { formatDatetimeTooltip } = useDateFormatters()

    const { verb, timestampKey } = STATUS_TRANSITION[invoice.status_label]
    const statusTooltip = formatDatetimeTooltip(
        verb,
        invoice[timestampKey] ?? invoice.last_status_transition_time ?? invoice.created_at,
    )

    const showTotal = shouldShowAttribute(displayedAttributes, "total") && invoice.total != null
    const showIssued = shouldShowAttribute(displayedAttributes, "date_issued") && Boolean(invoice.date_issued)
    const showTerms =
        shouldShowAttribute(displayedAttributes, "terms") && Boolean(invoice.date_issued) && Boolean(invoice.date_due)
    const showPaymentStatus =
        shouldShowAttribute(displayedAttributes, "payment_status") && isPayable(invoice.status_label)
    const showLastModified =
        shouldShowAttribute(displayedAttributes, "last_modified_at") && Boolean(invoice.last_modified_at)
    const showLabels = shouldShowAttribute(displayedAttributes, "labels") && invoice.labels.length > 0

    const hasAttributes = showTotal || showIssued || showTerms || showPaymentStatus || showLastModified || showLabels

    return (
        <ObjectCard
            onClick={onClick}
            isLoading={isLoading}
            isTabbable={isTabbable}
            className={className}
            {...errorProps}
        >
            <ObjectCard.Header
                id={invoice.custom_id ?? String(invoice.id)}
                showSeenIcon={Boolean(invoice.last_viewed)}
                statusBadge={
                    <ObjectStatusBadge
                        objectType="invoice"
                        status={invoice.status_label}
                        size="md"
                        tooltip={statusTooltip}
                    />
                }
            />
            <ObjectCard.Body primaryText={invoice.service_name} secondaryText={composeClientLocation(invoice)} />
            {(isLoading || hasAttributes) && (
                <ObjectCard.Attributes>
                    {showTotal && <TotalBadge value={invoice.total} />}
                    {showIssued && <IssueDateBadge date={invoice.date_issued} />}
                    {showTerms && (
                        <TermsBadge
                            variant="dates"
                            dateIssued={invoice.date_issued}
                            dateDue={invoice.date_due}
                            isExpired={invoice.is_overdue && OPEN_STATUS_LABELS.includes(invoice.status_label)}
                            label="Due"
                            expiredLabel="Due"
                        />
                    )}
                    {showPaymentStatus && (
                        <InvoicePaymentBadge
                            status={invoice.status_label}
                            total={invoice.total}
                            amountPaid={invoice.amount_paid}
                            amountUncollectible={invoice.amount_uncollectible}
                            amountDue={invoice.amount_due}
                        />
                    )}
                    {showLastModified && <LastModifiedBadge date={invoice.last_modified_at} />}
                    {showLabels && <LabelsBadges labels={invoice.labels} />}
                </ObjectCard.Attributes>
            )}
        </ObjectCard>
    )
}
